import React from 'react';
import ProgressCircle from '../ProgressCircle';
import { CourseData, UnitData } from 'course-types';
import ProgressUtilityService from '@/services/course/progress-utility-service';
import CourseUtilityService from '@/services/course/course-utility-service';
import UserProgressStorageService from '@/services/storage/user-progress-storage-service';

interface CourseProgressInfoProps {
  course: CourseData;
  units: Array<[string, UnitData]>;
}

export function CourseProgressInfo({ course, units }: CourseProgressInfoProps): React.JSX.Element {
  const progressPercentage = ProgressUtilityService.calculateCourseProgress(course.id);
  const totalActivities = CourseUtilityService.calculateTotalActivities(units);
  const progress = UserProgressStorageService.getUserCourseProgressCookie();
  let completedActivities = 0;

  if (progress && progress[course.id]) {
    const data = progress[course.id].completion_data;

    // Count completed activities in units
    Object.values(data.units).forEach((unit) => {
      // Count completed quizzes
      Object.values(unit.quiz ?? {}).forEach((quiz) => {
        if ((quiz as { attempts?: number }).attempts! > 0) completedActivities++;
      });

      // Count completed documents, videos, and assignments
      (['document', 'video', 'assignment'] as const).forEach((type) => {
        const collection = unit[type] ?? {};
        Object.values(collection).forEach((item) => {
          if ((item as { submissionDate?: Date }).submissionDate) completedActivities++;
        });
      });
    });

    // Count completed activities in subunits
    Object.values(data.subunits).forEach((subunit) => {
      // Count completed quizzes
      Object.values(subunit.quiz ?? {}).forEach((quiz) => {
        if ((quiz as { attempts?: number }).attempts! > 0) completedActivities++;
      });

      // Count completed documents, videos, and assignments
      (['document', 'video', 'assignment'] as const).forEach((type) => {
        const collection = subunit[type] ?? {};
        Object.values(collection).forEach((item) => {
          if ((item as { submissionDate?: Date }).submissionDate) completedActivities++;
        });
      });
    });
  }

  return (
    <div className='flex flex-col mb-[-16px] md:px-8 lg:px-12 px-4 items-start justify-center'>
      <h1 className='font-semibold text-2xl color-text_gray_900 mb-4'>{course.name}</h1>
      <div className='flex flex-row gap-2 items-center'>
        <ProgressCircle size={60} progress={progressPercentage} />
        <div className='flex flex-col'>
          <span className='text-text_gray_900 text-lg font-semibold'>Course Progress</span>
          <span className='text-text_gray_900 text-md font-normal'>
            {completedActivities} of {totalActivities} activities completed
          </span>
        </div>
      </div>
    </div>
  );
}
